<template>
  <div>
    <NavBar pageName="Client Services"/>

    <!-- Filters Card -->
    <v-container style="margin-top: 185px; max-width: 90%;">
      <!-- Header when viewing services for a specific SIM -->
      <v-card v-if="simData" class="mb-4 bg-primary" variant="flat">
        <v-card-text class="text-white py-3">
          <div class="d-flex justify-space-between align-center mb-2">
            <h3 class="text-h5">
              <v-icon class="mr-2">mdi-sim</v-icon>
              Servicii alocate SIM #{{ simData.id }}
            </h3>
            <v-btn
              v-if="filterSimId"
              color="white"
              variant="outlined"
              size="small"
              @click="goBackToSims"
            >
              <v-icon class="mr-1" size="small">mdi-arrow-left</v-icon>
              Inapoi la SIM-uri
            </v-btn>
          </div>
          <div class="d-flex flex-wrap ga-4 text-body-1">
            <span><strong>IMSI:</strong> {{ simData.imsi || '-' }}</span>
            <span><strong>Serial:</strong> {{ simData.serial || '-' }}</span>
            <span v-if="simServices.length > 0 && simServices[0].msisdn_number">
              <strong>MSISDN:</strong> {{ simServices[0].msisdn_number }}
            </span>
            <span v-if="simServices.length > 0">
              <strong>Client:</strong> {{ simServices[0].client_name }}
            </span>
          </div>
        </v-card-text>
      </v-card>

      <v-card class="mb-4 pa-4">
        <v-card-title class="d-flex align-center">
          Filtre
          <v-tooltip text="Rebuild client hierarchy cache" location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" icon="mdi-database-refresh" variant="text" size="small" class="ml-2" :loading="cacheRebuilding" @click="rebuildCache" />
            </template>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <!-- Row 1: Search -->
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="searchTerm"
                label="Cauta (min 3 caractere)"
                hint="Cauta dupa: Serial SIM, IMSI, MSISDN, nume serviciu, descriere"
                persistent-hint
                clearable
                prepend-inner-icon="mdi-magnify"
                @keyup.enter="search"
                @click="$event.target.select()"
                @dblclick="searchTerm = ''"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center flex-wrap ga-2">
              <v-btn color="primary" @click="search" :loading="loading">
                <v-icon class="mr-1">mdi-magnify</v-icon>
                Cauta
              </v-btn>
              <v-btn color="secondary" variant="outlined" @click="resetFilters">
                <v-icon class="mr-1">mdi-refresh</v-icon>
                Reset
              </v-btn>
              <v-btn
                v-if="showSimsButton"
                color="info"
                variant="elevated"
                @click="navigateToSimsWithFilters"
              >
                <v-icon class="mr-1">mdi-sim</v-icon>
                Vezi SIM-uri
              </v-btn>
            </v-col>
          </v-row>

          <!-- Row 2: Client Select + Include Subclients toggle -->
          <ClientSearchFilter
            v-model="selectedSubclientId"
            v-model:include-subclients="includeSubclientsToggle"
            :clients-hierarchy="clientsHierarchy"
            client-select-label="Client"
          />

          <!-- Row 3: Service Type Filter -->
          <v-row>
            <v-col cols="12">
              <ServiceTypeButtonFilter v-model="serviceTypeFilter" />
            </v-col>
          </v-row>

          <!-- Row 3.5: Service Select -->
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="selectedServiceId"
                :items="filteredServices"
                item-title="name"
                item-value="id"
                label="Serviciu"
                placeholder="Toate serviciile"
                clearable
                density="compact"
                hide-details
                :loading="servicesLoading"
                variant="outlined"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template v-slot:subtitle>
                      {{ item.raw.service_type_name }} | {{ item.raw.price || 0 }} {{ item.raw.currency_name || 'EUR' }}
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <!-- Row 4: Status Buttons -->
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-1 font-weight-bold mb-2">Status</div>
              <div class="d-flex flex-wrap ga-1">
                <v-btn
                  size="small"
                  :variant="selectAllExceptTerminated ? 'flat' : 'outlined'"
                  :color="selectAllExceptTerminated ? 'primary' : undefined"
                  @click="toggleAllExceptTerminated"
                >All except Terminated</v-btn>
                <v-btn
                  v-for="status in statuses"
                  :key="status.id"
                  size="small"
                  :variant="selectedStatuses.includes(status.id) ? 'flat' : 'outlined'"
                  :color="selectedStatuses.includes(status.id) ? 'primary' : undefined"
                  @click="toggleStatus(status.id)"
                >{{ status.name }}</v-btn>
                <v-btn
                  size="small"
                  :variant="selectAllStatuses ? 'flat' : 'outlined'"
                  :color="selectAllStatuses ? 'primary' : undefined"
                  @click="toggleAllStatuses"
                >All</v-btn>
              </div>
            </v-col>
          </v-row>

        </v-card-text>
      </v-card>

      <!-- SIM Panel (shown when searching by serial/IMSI) -->
      <v-card v-if="simPanelVisible && simData" class="mb-4 sim-panel">
        <v-card-title class="bg-primary text-white d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-2">mdi-sim</v-icon>
            SIM Gasit: {{ simData.serial }}
          </span>
          <div class="d-flex ga-2">
            <v-btn color="white" variant="outlined" size="small" @click="openAddServiceToSim">
              <v-icon left size="small">mdi-plus</v-icon>
              Adauga Serviciu
            </v-btn>
            <v-btn color="white" variant="text" size="small" @click="closeSimPanel">
              <v-icon size="small">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <!-- SIM Details -->
            <v-col cols="12" md="4">
              <div class="text-subtitle-2 font-weight-bold mb-2">Detalii SIM</div>
              <div><strong>Serial:</strong> {{ simData.serial }}</div>
              <div><strong>IMSI:</strong> {{ simData.imsi }}</div>
              <div v-if="simServices.length > 0 && simServices[0].msisdn_number">
                <strong>MSISDN:</strong> {{ simServices[0].msisdn_number }}
              </div>
              <div v-if="simServices.length > 0">
                <strong>Client:</strong> {{ simServices[0].client_name }}
              </div>
            </v-col>
            <!-- PIN/PUK -->
            <v-col cols="12" md="3">
              <div class="text-subtitle-2 font-weight-bold mb-2">PIN / PUK</div>
              <div><strong>PIN1:</strong> {{ simData.pin1 || '-' }}</div>
              <div><strong>PIN2:</strong> {{ simData.pin2 || '-' }}</div>
              <div><strong>PUK1:</strong> {{ simData.puk1 || '-' }}</div>
              <div><strong>PUK2:</strong> {{ simData.puk2 || '-' }}</div>
            </v-col>
            <!-- Services on this SIM -->
            <v-col cols="12" md="5">
              <div class="text-subtitle-2 font-weight-bold mb-2">Servicii pe acest SIM ({{ simServices.length }})</div>
              <div v-if="simServices.length === 0" class="text-grey">
                Niciun serviciu - poti adauga unul nou
              </div>
              <div v-for="svc in simServices" :key="svc.client_service_id" class="mb-2 pa-2 rounded sim-service-item" :class="getSimServiceClass(svc)">
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <strong>{{ svc.service_name }}</strong>
                    <v-chip size="x-small" :color="getStatusColor(svc.status_name)" class="ml-2">
                      {{ svc.status_name }}
                    </v-chip>
                  </div>
                  <div class="text-caption">
                    Exp: {{ formatDate(svc.expiration_date) }}
                  </div>
                </div>
                <div class="text-caption text-grey">
                  {{ svc.service_validity }} | {{ svc.service_price }} {{ svc.service_currency }}
                  <span v-if="svc.autorenew" class="ml-2 text-success">
                    <v-icon size="x-small">mdi-autorenew</v-icon> Autorenew
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Results Card -->
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Rezultate: {{ totalCount }} client services</span>
          <v-btn color="success" @click="openAddClientService">
            <v-icon left>mdi-plus</v-icon>
            Add Client Service
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            v-model:expanded="expanded"
            :headers="headers"
            :items="clientServices"
            :loading="loading"
            :items-per-page="perPage"
            v-model:sort-by="sortBy"
            @update:sort-by="onSortChange"
            show-expand
            item-value="id"
            :row-props="getRowProps"
            class="elevation-1"
          >
            <!-- ID Column -->
            <template v-slot:[`item.id`]="{ item }">
              {{ item.id }}
            </template>

            <!-- Nume (first 10 chars of client_short_name) - clickable -->
            <template v-slot:[`item.client_short_name`]="{ item }">
              <span
                class="clickable-link"
                @click.stop="openClientDetails(item.client_id)"
              >
                {{ truncateText(item.client_short_name, 10) }}
              </span>
            </template>

            <!-- Service Name - clickable -->
            <template v-slot:[`item.service_name`]="{ item }">
              <span
                class="clickable-link"
                @click.stop="openServiceDetails(item.service_id)"
              >
                {{ item.service_name }}
              </span>
            </template>

            <!-- Service Type with chip -->
            <template v-slot:[`item.service_type_name`]="{ item }">
              <v-chip size="small" :color="getServiceTypeColor(item.service_type_name)">
                {{ item.service_type_name }}
              </v-chip>
            </template>

            <!-- SIM Serial - clickable link to SIM services -->
            <template v-slot:[`item.serial_number`]="{ item }">
              <span
                v-if="item.sim_id && item.serial_number"
                class="clickable-link"
                @click.stop="navigateToSimServices(item.sim_id)"
              >
                {{ item.serial_number }}
              </span>
              <span v-else>-</span>
            </template>

            <!-- Created By - clickable -->
            <template v-slot:[`item.created_by_user_name`]="{ item }">
              <span
                v-if="item.created_by_user_name"
                class="clickable-link"
                @click.stop="goToUserSearch(item.created_by_user_name)"
              >
                {{ item.created_by_user_name }}
              </span>
              <span v-else>-</span>
            </template>

            <!-- Created At -->
            <template v-slot:[`item.created_at`]="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <!-- Expiration Date -->
            <template v-slot:[`item.expiration_date`]="{ item }">
              <template v-if="item.effective_expiration_date && item.effective_expiration_date !== item.expiration_date">
                <span
                  class="text-teal-darken-2 font-weight-medium clickable-date"
                  @click.stop="showContributions(item)"
                >
                  {{ formatDate(item.effective_expiration_date) }}
                  <v-icon size="x-small" class="ml-1">mdi-information-outline</v-icon>
                </span>
                <div class="text-caption text-grey">
                  (orig: {{ formatDate(item.expiration_date) }})
                </div>
              </template>
              <template v-else>
                {{ formatDate(item.expiration_date) }}
              </template>
            </template>

            <!-- Autorenew column -->
            <template v-slot:[`item.autorenew`]="{ item }">
              <v-chip
                size="small"
                :color="item.autorenew ? 'success' : 'grey'"
                :class="{ 'clickable-chip': canToggleAutorenew(item) }"
                @click.stop="canToggleAutorenew(item) ? confirmToggleAutorenew(item) : null"
              >
                {{ item.autorenew ? 'Yes' : 'No' }}
                <v-icon v-if="canToggleAutorenew(item)" size="x-small" class="ml-1">mdi-pencil</v-icon>
              </v-chip>
            </template>

            <!-- Status with chip -->
            <template v-slot:[`item.status_name`]="{ item }">
              <v-chip size="small" :color="getStatusColor(item.status_name)">
                {{ item.status_name }}
              </v-chip>
            </template>

            <!-- Actiuni column -->
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="canPayNow(item)"
                color="warning"
                size="x-small"
                class="mr-1"
                @click.stop="openPaymentModal(item)"
              >
                Pay now
              </v-btn>
              <v-btn
                v-if="canRenew(item)"
                color="success"
                size="x-small"
                class="mr-1"
                @click.stop="confirmRenew(item)"
                :loading="renewLoading && renewItem?.id === item.id"
              >
                Renew
              </v-btn>
              <v-btn
                v-if="canRenew(item)"
                color="primary"
                size="x-small"
                class="mr-1"
                @click.stop="openNewServiceDialog(item)"
              >
                New
              </v-btn>
              <v-btn
                v-if="canExtend(item)"
                color="teal"
                size="x-small"
                class="mr-1"
                @click.stop="confirmExtend(item)"
                :loading="extendLoading && extendItem?.id === item.id"
              >
                Extend
              </v-btn>
              <v-btn
                v-if="canSuspend(item)"
                color="orange"
                size="x-small"
                class="mr-1"
                @click.stop="confirmSuspend(item)"
                :loading="actionLoading && actionItem?.id === item.id"
              >
                Suspend
              </v-btn>
              <v-btn
                v-if="canReactivate(item)"
                color="success"
                size="x-small"
                class="mr-1"
                @click.stop="confirmReactivate(item)"
                :loading="actionLoading && actionItem?.id === item.id"
              >
                Reactivate
              </v-btn>
              <v-btn
                v-if="canTerminate(item)"
                color="warning"
                size="x-small"
                class="mr-1"
                @click.stop="confirmTerminate(item)"
                :loading="actionLoading && actionItem?.id === item.id"
              >
                Terminate
              </v-btn>
              <v-btn
                v-if="canRefund(item)"
                color="purple"
                size="x-small"
                class="mr-1"
                @click.stop="confirmRefund(item)"
                :loading="actionLoading && actionItem?.id === item.id"
              >
                Refund
              </v-btn>
              <v-btn
                v-if="canDelete(item)"
                color="error"
                size="x-small"
                @click.stop="confirmDelete(item)"
                :loading="deleteLoading && itemToDelete?.id === item.id"
              >
                Delete
              </v-btn>
              <span v-if="!canPayNow(item) && !canRenew(item) && !canExtend(item) && !canSuspend(item) && !canReactivate(item) && !canTerminate(item) && !canRefund(item) && !canDelete(item)">-</span>
            </template>

            <template v-slot:[`item.data-table-expand`]="{ toggleExpand, isExpanded, internalItem }">
              <v-btn
                size="x-small"
                color="success"
                :variant="isExpanded(internalItem) ? 'flat' : 'elevated'"
                @click="toggleExpand(internalItem)"
              >
                <v-icon size="small" class="mr-1">{{ isExpanded(internalItem) ? 'mdi-eye' : 'mdi-eye-outline' }}</v-icon>
                Info
              </v-btn>
            </template>

            <!-- Expanded Row -->
            <template v-slot:expanded-row="{ columns, item }">
              <tr>
                <td :colspan="columns.length" class="pa-4 bg-grey-lighten-4">
                  <ClientServiceInfo
                    :item="item"
                    :updating="updatingSimInfo && updateSimInfoItemId === item.id"
                    :comments-button-text="t.comments?.view_comments || 'View Comments'"
                    @update-sim-info="updateSimSubscriberInfo"
                  />
                </td>
              </tr>
            </template>
          </v-data-table>

          <!-- Toggle Active/All Services for SIM filter -->
          <div v-if="filterSimId" class="text-center mt-4">
            <v-btn
              v-if="filterActiveOnlyForSim"
              color="secondary"
              variant="outlined"
              @click="showAllSimServices"
            >
              <v-icon class="mr-2">mdi-eye</v-icon>
              Arata toate serviciile
            </v-btn>
            <v-btn
              v-else
              color="primary"
              variant="outlined"
              @click="showActiveSimServicesOnly"
            >
              <v-icon class="mr-2">mdi-filter</v-icon>
              Arata doar serviciile active
            </v-btn>
          </div>

          <!-- Pagination -->
          <v-pagination
            v-if="totalCount > perPage"
            v-model="page"
            :length="totalPages"
            @update:model-value="onPageChange"
            class="mt-4"
          ></v-pagination>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400" @keydown.enter="executeDelete">
      <v-card>
        <v-card-title>Confirma stergerea</v-card-title>
        <v-card-text>
          Esti sigur ca vrei sa stergi acest Client Service?
          <div v-if="itemToDelete" class="mt-2">
            <strong>ID:</strong> {{ itemToDelete.id }}<br>
            <strong>Service:</strong> {{ itemToDelete.service_name }}<br>
            <strong>Type:</strong> {{ itemToDelete.service_type_name }}
          </div>
          <div class="mt-2 text-caption">
            Aceasta actiune va elibera resursele asociate (MSISDN, IMSI).
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="deleteDialog = false">Anuleaza</v-btn>
          <v-btn color="error" @click="executeDelete" :loading="deleteLoading">Sterge</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Action Confirmation Dialog (Terminate/Reactivate/Suspend) -->
    <v-dialog v-model="actionDialog" max-width="400">
      <v-card>
        <v-card-title>{{ actionDialogTitle }}</v-card-title>
        <v-card-text>
          <span>{{ actionDialogMessage }}</span>
          <div v-if="actionItem" class="mt-2">
            <strong>ID:</strong> {{ actionItem.id }}<br>
            <strong>Service:</strong> {{ actionItem.service_name }}<br>
            <strong>Type:</strong> {{ actionItem.service_type_name }}<br>
            <strong>Client:</strong> {{ actionItem.client_name }}
          </div>
          <div v-if="actionType === 'terminate'" class="mt-2 text-caption text-warning">
            Aceasta actiune va dezactiva serviciul si resursele asociate.
          </div>
          <div v-if="actionType === 'suspend'" class="mt-2 text-caption text-orange">
            Serviciul va fi suspendat temporar si poate fi reactivat ulterior.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="actionDialog = false">Anuleaza</v-btn>
          <v-btn
            :color="actionDialogButtonColor"
            @click="executeAction"
            :loading="actionLoading"
          >
            {{ actionDialogButtonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Renew Confirmation Dialog -->
    <v-dialog v-model="renewDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-success text-white">Confirma reinnoirea serviciului</v-card-title>
        <v-card-text class="pt-4">
          <p>Doresti sa reinnoiesti urmatorul serviciu?</p>
          <div v-if="renewItem" class="mt-3 pa-3 bg-grey-lighten-4 rounded">
            <div><strong>Serviciu:</strong> {{ renewItem.service_name }}</div>
            <div><strong>Tip:</strong> {{ renewItem.service_type_name }}</div>
            <div><strong>Client:</strong> {{ renewItem.client_name }}</div>
            <div v-if="renewItem.msisdn_number"><strong>MSISDN:</strong> {{ renewItem.msisdn_number }}</div>
            <div v-if="renewItem.imsi_number"><strong>IMSI:</strong> {{ renewItem.imsi_number }}</div>
            <v-divider class="my-2"></v-divider>
            <div><strong>Perioada:</strong> {{ renewItem.service_validity || 'N/A' }}</div>
            <div class="text-h6 mt-2 text-success">
              <strong>Pret:</strong> {{ renewItem.service_price || 'N/A' }} {{ renewItem.service_currency || '' }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="renewDialog = false">Anuleaza</v-btn>
          <v-btn
            color="success"
            @click="executeRenew"
            :loading="renewLoading"
          >
            Reinnoieste
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Validity Contributions Dialog -->
    <v-dialog v-model="contributionsDialog" max-width="700">
      <v-card>
        <v-card-title class="bg-teal text-white">
          <v-icon class="mr-2">mdi-calendar-multiple</v-icon>
          Validity Contributions - CS #{{ contributionsItem?.id }}
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="contributionsLoading" class="text-center pa-4">
            <v-progress-circular indeterminate color="teal"></v-progress-circular>
          </div>
          <div v-else-if="contributions.length === 0" class="text-center pa-4 text-grey">
            No contributions found
          </div>
          <v-table v-else dense>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Service</th>
                <th>CS ID</th>
                <th>Days</th>
                <th>Effective Date</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, idx) in contributions" :key="c.id">
                <td>{{ idx + 1 }}</td>
                <td>
                  <v-chip size="x-small" :color="c.extend_cs_id ? 'orange' : 'teal'" variant="flat">
                    {{ c.extend_cs_id ? 'Extend' : 'Subscription' }}
                  </v-chip>
                </td>
                <td>{{ c.extend_cs_id ? c.extend_service_name : c.subscription_service_name }}</td>
                <td>{{ c.extend_cs_id || c.subscription_cs_id }}</td>
                <td class="font-weight-medium">+{{ c.validity_days }}d</td>
                <td class="font-weight-medium text-teal-darken-2">{{ formatDate(c.effective_date) }}</td>
                <td class="text-caption">{{ formatDate(c.created_at) }}</td>
              </tr>
            </tbody>
          </v-table>
          <div v-if="contributionsEffectiveDate" class="mt-3 pa-2 bg-teal-lighten-5 rounded text-right">
            <strong>Effective Expiration Date: {{ formatDate(contributionsEffectiveDate) }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="contributionsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Extend Service Confirmation Dialog -->
    <v-dialog v-model="extendDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-teal text-white">
          <v-icon class="mr-2">mdi-calendar-plus</v-icon>
          Extinde perioada serviciului
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Doresti sa extinzi perioada urmatorul serviciu?</p>
          <div v-if="extendItem" class="mt-3 pa-3 bg-grey-lighten-4 rounded">
            <div><strong>Serviciu:</strong> {{ extendItem.service_name }}</div>
            <div><strong>Tip:</strong> {{ extendItem.service_type_name }}</div>
            <div><strong>Client:</strong> {{ extendItem.client_name }}</div>
            <div v-if="extendItem.msisdn_number"><strong>MSISDN:</strong> {{ extendItem.msisdn_number }}</div>
            <div v-if="extendItem.imsi_number"><strong>IMSI:</strong> {{ extendItem.imsi_number }}</div>
            <v-divider class="my-2"></v-divider>
            <div><strong>Expira la:</strong> {{ formatDate(extendItem.expiration_date) }}</div>
            <div><strong>Perioada validitate:</strong> {{ extendItem.service_validity || 'N/A' }}</div>
            <div class="mt-2 text-teal-darken-2">
              <strong>Noua data expirare:</strong> {{ calculateNewExpirationDate(extendItem) }}
            </div>
            <div class="text-h6 mt-2 text-teal">
              <strong>Pret:</strong> {{ extendItem.service_price || '0' }} {{ extendItem.service_currency || '' }}
            </div>
          </div>
          <v-alert v-if="extendRequiresPayment" type="info" variant="tonal" density="compact" class="mt-3">
            <v-icon class="mr-1">mdi-credit-card</v-icon>
            Serviciul necesita plata online. Vei fi redirectionat catre pagina de plata.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="extendDialog = false">Anuleaza</v-btn>
          <v-btn
            color="teal"
            @click="executeExtend"
            :loading="extendLoading"
          >
            <v-icon class="mr-1">mdi-calendar-plus</v-icon>
            Extinde
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Autorenew Toggle Confirmation Dialog -->
    <v-dialog v-model="autorenewDialog" max-width="500">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-autorenew</v-icon>
          Confirma modificarea Autorenew
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">
            Esti sigur ca doresti <strong>{{ autorenewItem?.autorenew ? 'dezactivarea' : 'activarea' }}</strong> autorenew pentru acest serviciu?
          </p>
          <div v-if="autorenewItem" class="mt-3 pa-3 bg-grey-lighten-4 rounded">
            <div><strong>Serviciu:</strong> {{ autorenewItem.service_name }}</div>
            <div><strong>Client:</strong> {{ autorenewItem.client_name }}</div>
            <div><strong>Pret reinnoire:</strong> {{ autorenewItem.service_price || '0' }} {{ autorenewItem.service_currency || '' }}</div>
            <div><strong>Status curent Autorenew:</strong>
              <v-chip size="small" :color="autorenewItem.autorenew ? 'success' : 'grey'" class="ml-2">
                {{ autorenewItem.autorenew ? 'Activ' : 'Inactiv' }}
              </v-chip>
            </div>
            <div class="mt-2"><strong>Status nou:</strong>
              <v-chip size="small" :color="autorenewItem.autorenew ? 'grey' : 'success'" class="ml-2">
                {{ autorenewItem.autorenew ? 'Inactiv' : 'Activ' }}
              </v-chip>
            </div>
          </div>
          <v-alert
            v-if="!autorenewItem?.autorenew"
            type="info"
            variant="tonal"
            density="compact"
            class="mt-4"
          >
            <strong>Nota:</strong> Daca serviciul este prepaid, clientul va primi inainte de expirare o notificare sa isi plateasca serviciul pentru reinnoire.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="autorenewDialog = false">Nu</v-btn>
          <v-btn
            color="primary"
            @click="executeToggleAutorenew"
            :loading="autorenewLoading"
          >
            Da, {{ autorenewItem?.autorenew ? 'dezactiveaza' : 'activeaza' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" color="red" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Add New Client Service Dialog -->
    <AddNewClientService
      :show="showAddNewClientServiceDialog"
      :renewData="renewData"
      @close="onAddServiceDialogClose"
      @serviceAdded="onServiceAdded"
    />

    <!-- Client Details Dialog -->
    <v-dialog v-model="clientDetailsDialog" max-width="700">
      <v-card>
        <v-card-title class="bg-primary text-white">Detalii Client</v-card-title>
        <v-card-text class="pt-4">
          <div v-if="clientDetailsLoading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="clientDetails">
            <v-row>
              <v-col cols="12" md="6">
                <h4 class="mb-2">Informatii Generale</h4>
                <div><strong>ID:</strong> {{ clientDetails.id }}</div>
                <div><strong>Nume:</strong> {{ clientDetails.name }}</div>
                <div><strong>Short Name:</strong> {{ clientDetails.short_name || '-' }}</div>
                <div><strong>Status:</strong> {{ clientDetails.status }}</div>
                <div><strong>Client Type:</strong> {{ clientDetails.client_type_name }}</div>
                <div><strong>Billing Type:</strong> {{ clientDetails.billing_type_name }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <h4 class="mb-2">Contact</h4>
                <div><strong>Email:</strong> {{ clientDetails.email || '-' }}</div>
                <div><strong>Phone:</strong> {{ clientDetails.phone || '-' }}</div>
                <div><strong>Notification Email:</strong> {{ clientDetails.notification_email || '-' }}</div>
              </v-col>
            </v-row>
            <v-row v-if="clientDetails.company_name">
              <v-col cols="12">
                <h4 class="mb-2">Detalii Companie</h4>
                <div><strong>Company Name:</strong> {{ clientDetails.company_name }}</div>
                <div><strong>CUI:</strong> {{ clientDetails.cui || '-' }}</div>
                <div><strong>Registration Number:</strong> {{ clientDetails.registration_number || '-' }}</div>
                <div><strong>Bank:</strong> {{ clientDetails.bank || '-' }}</div>
                <div><strong>IBAN:</strong> {{ clientDetails.iban || '-' }}</div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <h4 class="mb-2">Financiar</h4>
                <div><strong>Max Credit:</strong> {{ clientDetails.max_credit }}</div>
                <div><strong>Current Debit:</strong> {{ clientDetails.current_debit }}</div>
                <div><strong>Current Credit:</strong> {{ clientDetails.current_credit }}</div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            variant="elevated"
            @click="navigateToClientEdit(clientDetails.id)"
          >
            <v-icon class="mr-1">mdi-account-edit</v-icon>
            Pagina client
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="red" @click="clientDetailsDialog = false">Inchide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Service Details Dialog -->
    <v-dialog v-model="serviceDetailsDialog" max-width="800">
      <v-card>
        <v-card-title class="bg-success text-white">Detalii Serviciu</v-card-title>
        <v-card-text class="pt-4">
          <div v-if="serviceDetailsLoading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="serviceDetails">
            <v-row>
              <v-col cols="12" md="6">
                <h4 class="mb-2">Informatii Generale</h4>
                <div><strong>ID:</strong> {{ serviceDetails.id }}</div>
                <div><strong>Name:</strong> {{ serviceDetails.name }}</div>
                <div><strong>Nickname:</strong> {{ serviceDetails.nickname || '-' }}</div>
                <div><strong>Public Name:</strong> {{ serviceDetails.public_name || '-' }}</div>
                <div><strong>Description:</strong> {{ serviceDetails.description || '-' }}</div>
                <div><strong>Service Type:</strong> {{ serviceDetails.service_type_name || serviceDetails.service_type_id }}</div>
                <div><strong>Service Status:</strong> {{ serviceDetails.service_status_name || serviceDetails.service_status_id || '-' }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <h4 class="mb-2">Preturi si Facturare</h4>
                <div><strong>Pret:</strong> {{ serviceDetails.service_price || '-' }} {{ serviceDetails.currency_name || '' }}</div>
                <div><strong>Currency:</strong> {{ serviceDetails.currency_name || serviceDetails.currency_id || '-' }}</div>
                <div><strong>Billing Type:</strong> {{ serviceDetails.billing_type_name || serviceDetails.billing_type_id || '-' }}</div>
                <div><strong>Billing Period:</strong> {{ serviceDetails.billing_period_name || serviceDetails.billing_period_id || '-' }}</div>
                <div><strong>Payment Method Type:</strong> {{ serviceDetails.payment_method_type_name || serviceDetails.payment_method_type_id || '-' }}</div>
                <div><strong>Validity:</strong> {{ serviceDetails.validity || '-' }}</div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h4 class="mb-2">Configurare</h4>
                <div><strong>Managed By Client:</strong> {{ serviceDetails.managed_by_client_name || serviceDetails.managed_by_client_id || '-' }}</div>
                <div><strong>Applied To Client Type:</strong> {{ serviceDetails.applied_to_client_type_name || serviceDetails.applied_to_client_type_id || '-' }}</div>
                <div><strong>Available To Subclients:</strong> {{ serviceDetails.available_to_subclients ? 'Da' : 'Nu' }}</div>
                <div><strong>Self Applied:</strong> {{ serviceDetails.self_applied ? 'Da' : 'Nu' }}</div>
                <div><strong>Allow Multiple:</strong> {{ serviceDetails.allow_multiple ? 'Da' : 'Nu' }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <h4 class="mb-2">Asocieri</h4>
                <div><strong>App:</strong> {{ serviceDetails.app_name || serviceDetails.app_id || '-' }}</div>
                <div><strong>MVNO:</strong> {{ serviceDetails.mvno_name || serviceDetails.mvno_id || '-' }}</div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <h4 class="mb-2">Audit</h4>
                <div><strong>Created By:</strong> {{ serviceDetails.created_by_user_name || serviceDetails.created_by_user_id || '-' }}</div>
                <div><strong>Created At:</strong> {{ formatDate(serviceDetails.created_at) }}</div>
                <div><strong>Updated At:</strong> {{ formatDate(serviceDetails.updated_at) }}</div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" @click="serviceDetailsDialog = false">Inchide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payment Modal (BT - Banca Transilvania) -->
    <v-dialog v-model="showPaymentModal" max-width="600px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center bg-primary text-white">
          <span class="text-h6">Finalizare plată</span>
          <v-btn icon variant="text" color="white" @click="closePaymentModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text-center pa-6">
          <div class="text-h6 text-primary mb-4">
            Vei fi direcționat către Banca Transilvania pentru plata online
          </div>

          <div class="text-body-1 mb-4">
            Comanda va fi activată după efectuarea plății.
          </div>

          <a :href="paymentLink" target="_blank" class="text-decoration-none" @click="onPaymentLinkClick">
            <div class="my-6">
              <img
                :src="btLogo"
                alt="Banca Transilvania"
                style="max-width: 250px;"
              />
            </div>

            <div class="text-h6 text-primary mb-4">
              {{ paymentOrderDetails }}
            </div>

            <v-btn color="warning" size="large" class="mt-4">
              Plătește online
            </v-btn>
          </a>

          <div class="text-h6 text-primary mt-6">
            Vei primi un email de confirmare după efectuarea plății!
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Subscriber Status Dialog -->
    <SubscriberStatusDialog
      :show="showSubscriberStatusDialog"
      :data="subscriberStatusData"
      :loading="updatingSimInfo"
      @close="showSubscriberStatusDialog = false"
    />

  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import AddNewClientService from "@/components/AddNewClientService.vue";
import SubscriberStatusDialog from "@/components/dialogs/SubscriberStatusDialog.vue";
import {getClientServicesList} from "@/composables/useGetClientServicesAPI";
import {deleteClientService, terminateClientService, reactivateClientService, suspendClientService, refundClientService, renewClientService, extendClientService, toggleAutorenew, getSimBySearch, getSimById} from "@/composables/useGetAllocatedMsisdnsAPI";
import {updateImsiInfo} from "@/composables/useGetAllocatedImsisAPI";
import {getClientDetails} from "@/composables/useGetClientsAPI";
import {getAllServicesList} from "@/composables/useGetServicesAPI";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import {log} from "@/composables/useLog";
import axios from "axios";
import {BASE_URL} from "@/composables/url_config";
import store from "@/store";
import btLogoImage from "@/assets/images/BTlogoLung.png";
import { t as tRef } from "@/composables/useLanguages";
import ClientSearchFilter from "@/components/ClientSearchFilter.vue";
import ClientServiceInfo from "@/components/ClientServiceInfo.vue";
import ServiceTypeButtonFilter from "@/components/ServiceTypeButtonFilter.vue";

export default {
  name: "ClientServicesList",
  components: {NavBar, AddNewClientService, ClientSearchFilter, ClientServiceInfo, SubscriberStatusDialog, ServiceTypeButtonFilter},
  data() {
    return {
      // Auth
      token: '',

      // Filters
      searchTerm: '',
      includeSubclientsToggle: false, // false = Own Only, true = Include Subclients
      selectedSubclientId: null,
      clientsHierarchy: [], // Hierarchical list of clients from API
      serviceTypeFilter: '20',  // default: imsi_subscription
      selectedServiceId: null, // Selected service for filtering
      services: [], // All services loaded from API
      servicesLoading: false,
      selectedStatuses: [], // Will be set after statuses load
      selectAllStatuses: false,
      selectAllExceptTerminated: true, // Default: all except terminated/deleted/failed
      filterStatusNames: null, // When set, filter by these status names after statuses load

      // Delete dialog
      deleteDialog: false,
      deleteLoading: false,
      itemToDelete: null,

      // Action dialog (Terminate/Reactivate)
      actionDialog: false,
      actionLoading: false,
      actionItem: null,
      actionType: null, // 'terminate' or 'reactivate'

      // Renew dialog
      renewDialog: false,
      renewLoading: false,
      renewItem: null,

      // Extend dialog
      extendDialog: false,
      extendLoading: false,
      extendItem: null,

      // Contributions dialog
      contributionsDialog: false,
      contributionsLoading: false,
      contributionsItem: null,
      contributions: [],
      contributionsEffectiveDate: null,

      // Data
      clientServices: [],
      totalCount: 0,
      loading: false,

      // Pagination
      page: 1,
      perPage: 20,

      // Sorting
      sortBy: [{key: 'expiration_date', order: 'asc'}],

      // Expanded rows
      expanded: [],

      // Lookup data
      serviceTypes: [],
      statuses: [],
      hasSubclients: false,

      // Snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // Add New Client Service Dialog
      showAddNewClientServiceDialog: false,

      // Renew
      renewData: null,

      // Client Details Dialog
      clientDetailsDialog: false,
      clientDetailsLoading: false,
      clientDetails: null,

      // Service Details Dialog
      serviceDetailsDialog: false,
      serviceDetailsLoading: false,
      serviceDetails: null,

      // Payment Modal
      showPaymentModal: false,
      paymentLink: '',
      paymentOrderDetails: '',
      paymentItem: null,
      btLogo: btLogoImage,

      // Autorenew Toggle
      autorenewDialog: false,
      autorenewLoading: false,
      autorenewItem: null,

      // Cache rebuild
      cacheRebuilding: false,

      // User permissions
      canEditClientServices: false,
      canRefundServices: false,

      // SIM Panel
      simPanelVisible: false,
      simData: null, // {id, imsi, serial, pin1, pin2, puk1, puk2}
      simServices: [], // client services on this SIM
      simAvailableServices: [], // available services to add

      // Filter by Sim ID (from URL query)
      filterSimId: null,
      filterActiveOnlyForSim: false, // When true, filter by active status only for sim_id

      // Update SIM info loading state
      updatingSimInfo: false,
      updateSimInfoItemId: null,

      // Subscriber Status Dialog
      showSubscriberStatusDialog: false,
      subscriberStatusData: null,

      // Auto-expand CS ID (from URL query cs_id)
      autoExpandCsId: null,

      // Table headers
      headers: [
        {title: 'ID', key: 'id', sortable: true},
        {title: 'Nume', key: 'client_short_name', sortable: true},
        {title: 'Service Name', key: 'service_name', sortable: true},
        {title: 'Service Type', key: 'service_type_name', sortable: true},
        {title: 'SIM', key: 'serial_number', sortable: true},
        {title: 'Created By', key: 'created_by_user_name', sortable: true},
        {title: 'Created At', key: 'created_at', sortable: true},
        {title: 'Expiration Date', key: 'expiration_date', sortable: true},
        {title: 'Autorenew', key: 'autorenew', sortable: true},
        {title: 'Status', key: 'status_name', sortable: true},
        {title: 'Actiuni', key: 'actions', sortable: false},
      ]
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage)
    },
    extendRequiresPayment() {
      // Check if extend requires payment (prepaid billing type and price > 0)
      if (!this.extendItem) return false
      const billingType = this.extendItem.billing_type_name?.toLowerCase()
      const price = parseFloat(this.extendItem.service_price) || 0
      return billingType === 'prepaid' && price > 0
    },
    isAdmin() {
      const user = store.getters.getUser
      return user?.is_admin || user?.admin || false
    },
    // Filter services based on selected service type and show_in_search
    filteredServices() {
      if (!this.services || this.services.length === 0) return []

      let results = this.services

      // Non-admin users only see services with show_in_search = true
      if (!this.isAdmin) {
        results = results.filter(s => s.show_in_search)
      }

      // If no service type selected, return filtered services
      if (this.serviceTypeFilter === 'all') {
        return results
      }

      // Filter by selected service type
      const selectedTypeId = parseInt(this.serviceTypeFilter, 10)
      return results.filter(s => s.service_type_id === selectedTypeId)
    },
    // Show "Vezi SIM-uri" button when SIM-related filter is active
    showSimsButton() {
      const hasValidSearch = this.searchTerm && this.searchTerm.trim().length >= 3

      // Check if a specific SIM-related service type is selected
      let hasSimServiceTypeFilter = false
      if (this.serviceTypeFilter !== 'all') {
        const selectedType = this.serviceTypes.find(st => st.id.toString() === this.serviceTypeFilter)
        if (selectedType) {
          const simTypes = ['imsi', 'msisdn', 'imsi_msisdn', 'imsi_subscription']
          if (simTypes.includes(selectedType.name?.toLowerCase())) {
            hasSimServiceTypeFilter = true
          }
        }
      }

      // Show button if:
      // 1. A specific SIM service type is selected (imsi, msisdn, imsi_msisdn, imsi_subscription)
      // 2. OR search term looks like IMSI/Serial/MSISDN
      if (hasSimServiceTypeFilter) {
        return true
      }

      if (hasValidSearch) {
        const term = this.searchTerm.trim()
        if (/^\d{10,15}$/.test(term) || /^\+?\d{10,15}$/.test(term)) {
          return true
        }
      }

      return false
    },
    actionDialogTitle() {
      const titles = {
        'terminate': 'Confirma terminarea',
        'reactivate': 'Confirma reactivarea',
        'suspend': 'Confirma suspendarea'
      }
      return titles[this.actionType] || 'Confirma actiunea'
    },
    actionDialogMessage() {
      const messages = {
        'terminate': 'Esti sigur ca vrei sa termini acest Client Service?',
        'reactivate': 'Esti sigur ca vrei sa reactivezi acest Client Service?',
        'suspend': 'Esti sigur ca vrei sa suspendezi acest Client Service?'
      }
      return messages[this.actionType] || ''
    },
    actionDialogButtonColor() {
      const colors = {
        'terminate': 'warning',
        'reactivate': 'success',
        'suspend': 'orange'
      }
      return colors[this.actionType] || 'primary'
    },
    actionDialogButtonText() {
      const texts = {
        'terminate': 'Termina',
        'reactivate': 'Reactiveaza',
        'suspend': 'Suspenda'
      }
      return texts[this.actionType] || 'Confirma'
    }
  },
  watch: {
    serviceTypeFilter() {
      // Reset selected service when service type filter changes
      this.selectedServiceId = null
    },
    // Watch for route changes (e.g., when cs_id param changes) - only for subsequent navigation
    '$route.query.cs_id'(newCsId, oldCsId) {
      log("Watcher cs_id triggered: newCsId=" + newCsId + ", oldCsId=" + oldCsId + ", token=" + (this.token ? 'set' : 'not set'))
      // Only trigger if token is set (component is fully loaded) and value changed
      if (newCsId && this.token && newCsId !== oldCsId) {
        log("Watcher cs_id: executing search for CS:" + newCsId)
        this.searchTerm = `CS:${newCsId}`
        this.selectAllStatuses = true
        this.selectAllExceptTerminated = false
        this.selectedStatuses = []
        this.serviceTypeFilter = 'all'
        this.autoExpandCsId = parseInt(newCsId, 10)
        this.search()
      }
    }
  },
  methods: {
    async search() {
      this.loading = true
      this.page = 1

      // Check if search term looks like a SIM serial/IMSI (numeric and 5+ chars)
      const term = this.searchTerm?.trim() || ''
      const looksLikeSimSearch = term.length >= 5 && /^\d+$/.test(term)

      if (looksLikeSimSearch) {
        // Try to find a specific SIM
        await this.searchForSim(term)
      } else if (!this.filterSimId) {
        // Hide SIM panel for regular searches ONLY if we don't have a sim_id filter from URL
        // If filterSimId is set, keep the SIM context
        this.simPanelVisible = false
        this.simData = null
        this.simServices = []
      }
      // If filterSimId is set, keep simData and simServices to maintain SIM context

      await this.fetchClientServices()
      this.loading = false
    },

    async searchForSim(searchTerm) {
      log("Searching for SIM with term: " + searchTerm)

      // Build options with status filter
      const options = {}
      if (this.selectedStatuses.length > 0 && !this.selectAllStatuses) {
        options.status_ids = this.selectedStatuses
      }

      const result = await getSimBySearch(this.token, searchTerm, options)

      if (result.error) {
        log("Error searching for SIM: " + result.message)
        this.simPanelVisible = false
        return
      }

      if (result.found) {
        this.simData = result.imsi
        this.simServices = result.client_services || []
        this.simAvailableServices = result.available_services || []
        this.simPanelVisible = true
        log("Found SIM with " + this.simServices.length + " services")
      } else {
        this.simPanelVisible = false
        this.simData = null
        this.simServices = []
      }
    },

    async fetchClientServices() {
      const options = {
        page: this.page,
        per_page: this.perPage,
        sort_by: this.sortBy[0]?.key || 'expiration_date',
        sort_order: this.sortBy[0]?.order || 'asc'
      }

      // Search term
      if (this.searchTerm && this.searchTerm.length >= 3) {
        options.search_term = this.searchTerm
      }

      // Subclients
      if (this.hasSubclients) {
        options.include_subclients = this.includeSubclientsToggle
        if (this.selectedSubclientId) {
          options.subclient_id = this.selectedSubclientId
        }
      }

      // Service type filter
      if (this.serviceTypeFilter !== 'all') {
        options.service_type_id = this.serviceTypeFilter
      }

      // Specific service filter
      if (this.selectedServiceId) {
        options.service_id = this.selectedServiceId
      }

      // Status IDs
      if (this.selectedStatuses.length > 0 && !this.selectAllStatuses) {
        options.status_ids = this.selectedStatuses
      }

      // Filter by Sim ID (from URL query)
      if (this.filterSimId) {
        options.sim_id = this.filterSimId
      }

      log("Fetching client services with options:", options)

      const result = await getClientServicesList(this.token, options)

      if (result.error) {
        this.showSnackbar(result.message, 'error')
        return
      }

      // Update filters if first load
      const isFirstLoad = this.serviceTypes.length === 0
      if (isFirstLoad) {
        this.serviceTypes = result.filters.service_types
        this.statuses = result.filters.statuses
        // Load hierarchical clients list
        if (this.clientsHierarchy.length === 0) {
          this.clientsHierarchy = result.filters.clients_hierarchy || []
          this.hasSubclients = result.filters.has_subclients
        }
        // Apply status filter by names (from msisdn_id query param)
        if (this.filterStatusNames && this.filterStatusNames.length > 0) {
          this.selectedStatuses = this.statuses
            .filter(s => this.filterStatusNames.includes(s.name))
            .map(s => s.id)
          this.filterStatusNames = null
          await this.fetchClientServices()
          return
        }
        // Apply active-only filter for SIM services if requested - re-fetch with filter
        if (this.filterActiveOnlyForSim && this.filterSimId) {
          const activeStatus = this.statuses.find(s => s.name === 'active')
          if (activeStatus) {
            this.selectedStatuses = [activeStatus.id]
            this.selectAllExceptTerminated = false
            // Re-fetch with the active filter applied (don't show unfiltered results)
            await this.fetchClientServices()
            return
          }
        }
        // Apply default "all except terminated" if flag is set and selectedStatuses is empty
        if (this.selectAllExceptTerminated && this.selectedStatuses.length === 0) {
          this.selectedStatuses = this.statuses
            .filter(s => !['terminated', 'deleted', 'failed'].includes(s.name))
            .map(s => s.id)
          // Re-fetch with the proper filter applied
          await this.fetchClientServices()
          return
        }
      }

      // Set results (only after filter is properly applied)
      this.clientServices = result.client_services
      this.totalCount = result.total_count

      // Auto-expand CS if searching by cs_id
      if (this.autoExpandCsId) {
        const foundCs = this.clientServices.find(cs => cs.id === this.autoExpandCsId)
        if (foundCs) {
          // Expand this row
          this.expanded = [foundCs.id]
          // Clear the auto-expand flag
          this.autoExpandCsId = null
        }
      }
    },

    async loadServices() {
      this.servicesLoading = true
      try {
        const result = await getAllServicesList(this.token)
        if (!result.error && result.services_list) {
          // Parse services_list - it comes as JSON string from API
          if (typeof result.services_list === 'string') {
            this.services = JSON.parse(result.services_list)
          } else if (Array.isArray(result.services_list)) {
            this.services = result.services_list
          }
          log("Loaded " + this.services.length + " services for dropdown")
        }
      } catch (err) {
        log("Error loading services:", err)
      } finally {
        this.servicesLoading = false
      }
    },

    resetFilters() {
      this.searchTerm = ''
      this.includeSubclientsToggle = false
      this.selectedSubclientId = null
      this.serviceTypeFilter = '20' // default: imsi_subscription
      this.selectedServiceId = null
      this.selectAllStatuses = false
      this.selectAllExceptTerminated = true
      this.selectedStatuses = this.statuses
        .filter(s => !['terminated', 'deleted', 'executed', 'failed'].includes(s.name))
        .map(s => s.id)
      this.page = 1
      this.sortBy = [{key: 'expiration_date', order: 'asc'}]
      // Reset SIM panel
      this.simPanelVisible = false
      this.simData = null
      this.simServices = []
      this.simAvailableServices = []
      // Reset SIM filter and clear URL param
      this.filterSimId = null
      if (this.$route.query.sim_id) {
        this.$router.replace({ query: {} })
      }
      this.search()
    },

    toggleAllStatuses() {
      this.selectAllStatuses = !this.selectAllStatuses
      if (this.selectAllStatuses) {
        this.selectedStatuses = this.statuses.map(s => s.id)
        this.selectAllExceptTerminated = false
      } else {
        // Deselect all, fall back to Active only
        const activeStatus = this.statuses.find(s => s.name === 'active')
        this.selectedStatuses = activeStatus ? [activeStatus.id] : []
        this.selectAllExceptTerminated = false
      }
    },

    toggleAllExceptTerminated() {
      this.selectAllExceptTerminated = !this.selectAllExceptTerminated
      if (this.selectAllExceptTerminated) {
        this.selectedStatuses = this.statuses
          .filter(s => !['terminated', 'deleted', 'executed', 'failed'].includes(s.name))
          .map(s => s.id)
        this.selectAllStatuses = false
      } else {
        // Deselect, fall back to Active only
        const activeStatus = this.statuses.find(s => s.name === 'active')
        this.selectedStatuses = activeStatus ? [activeStatus.id] : []
        this.selectAllStatuses = false
      }
    },

    toggleStatus(id) {
      const index = this.selectedStatuses.indexOf(id)
      if (index > -1) {
        this.selectedStatuses.splice(index, 1)
      } else {
        this.selectedStatuses.push(id)
      }
      // Update special button states
      this.selectAllStatuses = this.selectedStatuses.length === this.statuses.length
      const nonTerminatedIds = this.statuses
        .filter(s => !['terminated', 'deleted', 'executed', 'failed'].includes(s.name))
        .map(s => s.id)
      this.selectAllExceptTerminated = nonTerminatedIds.length === this.selectedStatuses.length &&
        nonTerminatedIds.every(sid => this.selectedStatuses.includes(sid))
    },

    canDelete(item) {
      // Can delete if status is pending_payment and type is client, msisdn, imsi, or imsi_msisdn
      const statusName = item.status_name?.toLowerCase()
      const typeName = item.service_type_name?.toLowerCase()
      const allowedTypes = ['client', 'msisdn', 'imsi', 'imsi_msisdn']
      return statusName === 'pending_payment' && allowedTypes.includes(typeName)
    },

    canPayNow(item) {
      // Can pay now if status is pending_payment and has bt_form_url
      const statusName = item.status_name?.toLowerCase()
      return statusName === 'pending_payment' && item.bt_form_url
    },

    openPaymentModal(item) {
      this.paymentItem = item
      this.paymentLink = item.bt_form_url
      this.paymentOrderDetails = `Serviciu: ${item.service_name} pentru ${item.client_name}`
      this.showPaymentModal = true
    },

    closePaymentModal() {
      this.showPaymentModal = false
      this.paymentLink = ''
      this.paymentOrderDetails = ''
      this.paymentItem = null
    },

    onPaymentLinkClick() {
      // User clicked the payment link - close modal after a short delay
      setTimeout(() => {
        this.closePaymentModal()
        // Refresh the list to check if payment was completed
        this.fetchClientServices()
      }, 500)
    },

    isOneShotService(item) {
      // credit and valability services are one-shot operations, not manageable subscriptions
      const typeName = item.service_type_name?.toLowerCase()
      return typeName === 'credit' || typeName === 'valability'
    },

    canRenew(item) {
      if (this.isOneShotService(item)) return false
      // Can renew if status is expired
      const statusName = item.status_name?.toLowerCase()
      return statusName === 'expired'
    },

    confirmRenew(item) {
      // Show renew confirmation dialog
      this.renewItem = item
      this.renewDialog = true
    },

    async executeRenew() {
      if (!this.renewItem) return

      this.renewLoading = true
      try {
        const result = await renewClientService(this.token, this.renewItem.id)
        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la reinnoire', 'error')
        } else {
          this.showSnackbar('Serviciu reinnoit cu succes', 'success')
          this.renewDialog = false
          this.renewItem = null
          await this.fetchClientServices()
        }
      } catch (err) {
        log("Error in executeRenew:", err)
        this.showSnackbar('Eroare la reinnoire', 'error')
      } finally {
        this.renewLoading = false
      }
    },

    canExtend(item) {
      if (this.isOneShotService(item)) return false
      // Can extend if has expiration date and status is active or expired
      if (!item.expiration_date) return false
      const statusName = item.status_name?.toLowerCase()
      return statusName === 'active' || statusName === 'expired'
    },

    async showContributions(item) {
      this.contributionsItem = item
      this.contributions = []
      this.contributionsEffectiveDate = null
      this.contributionsDialog = true
      this.contributionsLoading = true
      try {
        const headers = {
          "Authorization": `Bearer ${this.token}`,
          "Content-Type": "application/json"
        }
        const result = await axios.get(BASE_URL + '/home/get_cs_validity_contributions_api', {
          headers,
          params: { client_service_id: item.id }
        })
        if (result.data && !result.data.error) {
          this.contributions = result.data.contributions || []
          this.contributionsEffectiveDate = result.data.effective_expiration_date
        }
      } catch (err) {
        log("Error fetching contributions: " + err)
      }
      this.contributionsLoading = false
    },

    confirmExtend(item) {
      this.extendItem = item
      this.extendDialog = true
    },

    calculateNewExpirationDate(item) {
      if (!item || !item.expiration_date) return 'N/A'
      // Parse validity from service_validity (e.g., "30 days", "1 month", "1 year")
      const validityStr = item.service_validity || ''
      let daysToAdd = 30 // default

      if (validityStr.includes('day')) {
        const match = validityStr.match(/(\d+)/)
        if (match) daysToAdd = parseInt(match[1], 10)
      } else if (validityStr.includes('month')) {
        const match = validityStr.match(/(\d+)/)
        if (match) daysToAdd = parseInt(match[1], 10) * 30
      } else if (validityStr.includes('year')) {
        const match = validityStr.match(/(\d+)/)
        if (match) daysToAdd = parseInt(match[1], 10) * 365
      }

      const currentExpiration = new Date(item.expiration_date)
      const newExpiration = new Date(currentExpiration)
      newExpiration.setDate(newExpiration.getDate() + daysToAdd)
      return newExpiration.toLocaleDateString('ro-RO')
    },

    async executeExtend() {
      if (!this.extendItem) return

      this.extendLoading = true
      try {
        const result = await extendClientService(this.token, this.extendItem.id)
        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la extindere', 'error')
        } else {
          // Check if payment is required
          if (result.requires_payment && result.payment_url) {
            // Show payment modal
            this.paymentLink = result.payment_url
            this.paymentOrderDetails = `Extindere ${this.extendItem.service_name}`
            this.paymentItem = this.extendItem
            this.showPaymentModal = true
            this.showSnackbar('Serviciu extins - necesita plata', 'info')
          } else {
            this.showSnackbar(result.message || 'Serviciu extins cu succes', 'success')
          }
          this.extendDialog = false
          this.extendItem = null
          await this.fetchClientServices()
        }
      } catch (err) {
        log("Error in executeExtend:", err)
        this.showSnackbar('Eroare la extindere', 'error')
      } finally {
        this.extendLoading = false
      }
    },

    openNewServiceDialog(item) {
      // Prepare data for new service based on expired service
      this.renewData = {
        clientId: item.client_id,
        clientName: item.client_name,
        serviceId: item.service_id,
        serviceName: item.service_name,
        serviceTypeId: item.service_type_id,
        serviceTypeName: item.service_type_name,
        description: item.description,
        // Technical data
        msisdnId: item.msisdn_id,
        msisdnNumber: item.msisdn_number,
        imsiId: item.imsi_id,
        imsiNumber: item.imsi_number,
        // Original CS ID for reference
        originalClientServiceId: item.id
      }
      this.showAddNewClientServiceDialog = true
    },

    canTerminate(item) {
      if (this.isOneShotService(item)) return false
      // Can terminate if status is active or expired
      const statusName = item.status_name?.toLowerCase()
      return statusName === 'active' || statusName === 'expired'
    },

    canReactivate(item) {
      if (this.isOneShotService(item)) return false
      // Can reactivate if status is suspended
      const statusName = item.status_name?.toLowerCase()
      return statusName === 'suspended'
    },

    canSuspend(item) {
      if (this.isOneShotService(item)) return false
      // Can suspend if status is active
      const statusName = item.status_name?.toLowerCase()
      return statusName === 'active'
    },

    confirmTerminate(item) {
      this.actionItem = item
      this.actionType = 'terminate'
      this.actionDialog = true
    },

    confirmReactivate(item) {
      this.actionItem = item
      this.actionType = 'reactivate'
      this.actionDialog = true
    },

    confirmSuspend(item) {
      this.actionItem = item
      this.actionType = 'suspend'
      this.actionDialog = true
    },

    canRefund(item) {
      // Can refund if user has permission, status is active/expired/terminated, and created in current month
      if (!this.canRefundServices) return false
      const statusName = item.status_name?.toLowerCase()
      if (statusName !== 'active') return false
      // Check if created in current month
      if (!item.created_at) return false
      const createdAt = new Date(item.created_at)
      const now = new Date()
      return createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear()
    },

    confirmRefund(item) {
      this.actionItem = item
      this.actionType = 'refund'
      this.actionDialog = true
    },

    async executeAction() {
      if (!this.actionItem || !this.actionType) return

      this.actionLoading = true
      try {
        const result = await this.callActionAPI(this.actionItem.id, this.actionType)
        if (result.error) {
          this.showSnackbar(result.message || `Eroare la ${this.actionType}`, 'error')
        } else {
          const actionLabels = {
            'terminate': 'terminat',
            'reactivate': 'reactivat',
            'suspend': 'suspendat',
            'refund': 'rambursat'
          }
          const actionLabel = actionLabels[this.actionType] || this.actionType
          this.showSnackbar(`Client Service ${actionLabel} cu succes`, 'success')
          this.actionDialog = false
          this.actionItem = null
          this.actionType = null
          await this.fetchClientServices()
          // Refresh SIM panel data so service availability is updated
          if (this.filterSimId) {
            await this.loadSimById(this.filterSimId)
          }
        }
      } catch (err) {
        log("Error in executeAction:", err)
        this.showSnackbar(`Eroare la ${this.actionType}`, 'error')
      } finally {
        this.actionLoading = false
      }
    },

    async callActionAPI(clientServiceId, action) {
      // Call the API for terminate, reactivate, suspend, or refund using composable functions
      const actionFunctions = {
        'terminate': terminateClientService,
        'reactivate': reactivateClientService,
        'suspend': suspendClientService,
        'refund': refundClientService
      }
      const actionFunction = actionFunctions[action]

      if (!actionFunction) {
        return { error: true, message: 'Unknown action' }
      }

      return await actionFunction(this.token, clientServiceId)
    },

    confirmDelete(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    async executeDelete() {
      if (!this.itemToDelete) return

      this.deleteLoading = true
      try {
        const result = await deleteClientService(this.token, this.itemToDelete.id)
        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la stergere', 'error')
        } else {
          this.showSnackbar('Client Service sters cu succes', 'success')
          this.deleteDialog = false
          this.itemToDelete = null
          // Refresh the list
          await this.fetchClientServices()
          if (this.filterSimId) {
            await this.loadSimById(this.filterSimId)
          }
        }
      } catch (err) {
        log("Error in executeDelete:", err)
        this.showSnackbar('Eroare la stergere', 'error')
      } finally {
        this.deleteLoading = false
      }
    },

    openAddClientService() {
      // If we have a SIM filter active, use the SIM service addition flow
      if (this.filterSimId && this.simData && this.simServices.length > 0) {
        this.openAddServiceToSim()
      } else {
        // General flow - reset renewData and open dialog
        this.renewData = null
        this.showAddNewClientServiceDialog = true
      }
    },

    onAddServiceDialogClose() {
      this.showAddNewClientServiceDialog = false
      this.renewData = null
    },

    async onServiceAdded(data) {
      this.showAddNewClientServiceDialog = false
      this.renewData = null
      this.showSnackbar('Client Service adaugat cu succes', 'success')

      // If a SIM was selected (normal mode with SIM selector), navigate to its active services
      const simId = data?.simId
      if (simId && !this.filterSimId) {
        this.$router.push({
          name: 'clientServices',
          query: { sim_id: simId, status: 'active' }
        })
        return
      }

      await this.fetchClientServices()
      if (this.filterSimId) {
        await this.loadSimById(this.filterSimId)
      }
    },

    // Autorenew toggle methods
    canToggleAutorenew(item) {
      if (this.isOneShotService(item)) return false
      // Can toggle if user has permission, status is active or expired, and service type is SIM-related
      if (!this.canEditClientServices) return false
      const statusName = item.status_name?.toLowerCase()
      const serviceType = item.service_type_name?.toLowerCase()
      const allowedServiceTypes = ['msisdn', 'imsi_msisdn', 'imsi_subscription', 'imsi']
      return (statusName === 'active' || statusName === 'expired') && allowedServiceTypes.includes(serviceType)
    },

    confirmToggleAutorenew(item) {
      this.autorenewItem = item
      this.autorenewDialog = true
    },

    async executeToggleAutorenew() {
      if (!this.autorenewItem) return

      this.autorenewLoading = true
      try {
        const result = await toggleAutorenew(this.token, this.autorenewItem.id)
        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la modificarea autorenew', 'error')
        } else {
          this.showSnackbar(result.message || 'Autorenew modificat cu succes', 'success')
          this.autorenewDialog = false
          this.autorenewItem = null
          await this.fetchClientServices()
        }
      } catch (err) {
        log("Error in executeToggleAutorenew:", err)
        this.showSnackbar('Eroare la modificarea autorenew', 'error')
      } finally {
        this.autorenewLoading = false
      }
    },

    onSortChange(newSort) {
      this.sortBy = newSort
      this.fetchClientServices()
    },

    onPageChange(newPage) {
      this.page = newPage
      this.fetchClientServices()
    },

    getRowProps(item) {
      const statusName = item.item?.status_name?.toLowerCase()
      if (statusName === 'pending_payment') {
        return {style: 'background-color: #FFEBEE;'}
      }
      if (statusName === 'terminated' || statusName === 'deleted') {
        return {style: 'background-color: #EEEEEE;'}
      }
      if (statusName === 'expired') {
        return {style: 'background-color: #FFF3E0;'}
      }
      return {}
    },

    getStatusColor(status) {
      const statusLower = status?.toLowerCase()
      switch (statusLower) {
        case 'active':
          return 'success'
        case 'pending_payment':
          return 'error'
        case 'expired':
          return 'warning'
        case 'suspended':
          return 'orange'
        case 'executed':
          return 'success'
        case 'failed':
          return 'error'
        case 'terminated':
        case 'deleted':
          return 'grey'
        case 'blocked':
          return 'red-darken-4'
        default:
          return 'info'
      }
    },

    getServiceTypeColor(type) {
      const typeLower = type?.toLowerCase()
      switch (typeLower) {
        case 'imsi':
          return 'blue'
        case 'msisdn':
          return 'green'
        case 'imsi_msisdn':
          return 'purple'
        case 'client':
          return 'teal'
        default:
          return 'grey'
      }
    },

    getSimServiceClass(svc) {
      const status = svc.status_name?.toLowerCase()
      switch (status) {
        case 'active':
          return 'bg-green-lighten-5'
        case 'expired':
          return 'bg-orange-lighten-5'
        case 'suspended':
          return 'bg-yellow-lighten-5'
        case 'terminated':
        case 'deleted':
          return 'bg-grey-lighten-3'
        default:
          return 'bg-grey-lighten-5'
      }
    },

    openAddServiceToSim() {
      if (!this.simData || this.simServices.length === 0) {
        this.showSnackbar('Nu s-au putut determina datele SIM-ului', 'error')
        return
      }

      // Get client info from first service on this SIM
      const firstService = this.simServices[0]

      // Prepare data for AddNewClientService
      this.renewData = {
        clientId: firstService.client_id,
        clientName: firstService.client_name,
        // Pre-fill with IMSI and MSISDN from SIM
        imsiId: this.simData.id,
        imsiNumber: this.simData.imsi,
        msisdnId: firstService.msisdn_id,
        msisdnNumber: firstService.msisdn_number,
        // Mark as SIM service addition
        isSimServiceAddition: true,
        // Pass available services for filtering
        availableServices: this.simAvailableServices,
        // Pass existing services for allow_multiple check
        existingServices: this.simServices
      }
      this.showAddNewClientServiceDialog = true
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('ro-RO')
    },

    truncateText(text, maxLength) {
      if (!text) return '-'
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    async rebuildCache() {
      this.cacheRebuilding = true
      try {
        const { rebuildHierarchyCache } = await import('@/composables/useHierarchyCacheAPI')
        const result = await rebuildHierarchyCache(this.token)
        if (!result.error) {
          this.showSnackbar('Cache rebuild started successfully', 'success')
        } else {
          this.showSnackbar(result.message || 'Error rebuilding cache', 'error')
        }
      } catch (e) {
        this.showSnackbar('Error rebuilding cache', 'error')
      } finally {
        this.cacheRebuilding = false
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },

    // Open Client Details Dialog
    async openClientDetails(clientId) {
      if (!clientId) return
      this.clientDetailsDialog = true
      this.clientDetailsLoading = true
      this.clientDetails = null

      try {
        const result = await getClientDetails(this.token, clientId)
        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la incarcarea detaliilor clientului', 'error')
          this.clientDetailsDialog = false
        } else {
          this.clientDetails = result.client_details
        }
      } catch (err) {
        log("Error in openClientDetails:", err)
        this.showSnackbar('Eroare la incarcarea detaliilor clientului', 'error')
        this.clientDetailsDialog = false
      } finally {
        this.clientDetailsLoading = false
      }
    },

    // Open Service Details Dialog
    async openServiceDetails(serviceId) {
      if (!serviceId) return
      this.serviceDetailsDialog = true
      this.serviceDetailsLoading = true
      this.serviceDetails = null

      try {
        const result = await getAllServicesList(this.token)
        log("getAllServicesList result:", result)
        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la incarcarea detaliilor serviciului', 'error')
          this.serviceDetailsDialog = false
        } else {
          // Parse services_list - it comes as JSON string from API
          let servicesList = []
          if (typeof result.services_list === 'string') {
            servicesList = JSON.parse(result.services_list)
          } else if (Array.isArray(result.services_list)) {
            servicesList = result.services_list
          }
          log("Parsed servicesList:", servicesList)
          log("Looking for serviceId:", serviceId)
          this.serviceDetails = servicesList.find(s => s.id === serviceId)
          if (!this.serviceDetails) {
            this.showSnackbar('Serviciul nu a fost gasit', 'error')
            this.serviceDetailsDialog = false
          }
        }
      } catch (err) {
        log("Error in openServiceDetails:", err)
        this.showSnackbar('Eroare la incarcarea detaliilor serviciului', 'error')
        this.serviceDetailsDialog = false
      } finally {
        this.serviceDetailsLoading = false
      }
    },

    // Navigate to Users page with search query
    goToUserSearch(userName) {
      if (!userName) return
      this.$router.push({
        name: 'users',
        query: { search: userName }
      })
    },

    // Navigate to Client Edit page
    navigateToClientEdit(clientId) {
      if (!clientId) return
      this.clientDetailsDialog = false
      this.$router.push({name: 'editClient', query: {id: clientId}})
    },

    // Filter ClientServicesList by SIM ID (show active services for this SIM)
    async navigateToSimServices(simId) {
      if (!simId) return
      // Set SIM filter directly (we're already on the same page)
      this.filterSimId = simId
      this.filterActiveOnlyForSim = true
      this.selectAllStatuses = false
      this.selectAllExceptTerminated = false
      this.selectedStatuses = []
      this.searchTerm = ''
      this.simPanelVisible = false
      // Update URL
      this.$router.replace({ query: { sim_id: simId, status: 'active' } })
      // Load SIM details and refresh list
      await this.loadSimById(simId)
      await this.fetchClientServices()
    },

    // Navigate to SimsList with search by SIM ID
    navigateToSimsList(simId) {
      if (!simId) return
      this.$router.push({
        name: 'sims',
        query: { search: simId.toString() }
      })
    },

    // Navigate to SimsList with current filters applied
    navigateToSimsWithFilters() {
      const query = {}

      // Pass search term if exists
      if (this.searchTerm && this.searchTerm.length >= 3) {
        query.search = this.searchTerm.trim()
      }

      // Pass client filter if selected
      if (this.selectedSubclientId) {
        query.client_id = this.selectedSubclientId
      }

      // Include subclients setting
      if (this.includeSubclientsToggle) {
        query.include_subclients = 'true'
      }

      this.$router.push({
        name: 'sims',
        query
      })
    },

    // Navigate to ImsisList with search term (IMSI or Serial)
    navigateToImsisList(searchTerm) {
      if (!searchTerm) return
      this.$router.push({
        path: '/imsisList',
        query: { search: searchTerm }
      })
    },

    // Update SIM subscriber info from mobile network
    async updateSimSubscriberInfo(item) {
      if (!item || !item.sim_id) {
        this.showSnackbar('Nu exista SIM ID pentru acest serviciu', 'error')
        return
      }

      this.updatingSimInfo = true
      this.updateSimInfoItemId = item.id

      try {
        // We need the IMSI ID (which might be the same as sim_id based on your data model)
        // Let's use the imsi_id if available, otherwise try sim_id
        const imsiId = item.imsi_id || item.sim_id

        const result = await updateImsiInfo(this.token, imsiId)
        if (result.error) {
          this.showSnackbar(result.message || 'Eroare la actualizarea informatiilor SIM', 'error')
        } else {
          // Store the result and open the dialog
          this.subscriberStatusData = result
          this.showSubscriberStatusDialog = true
          // Refresh the list to show updated data
          await this.fetchClientServices()
        }
      } catch (err) {
        log("Error in updateSimSubscriberInfo:", err)
        this.showSnackbar('Eroare la actualizarea informatiilor SIM', 'error')
      } finally {
        this.updatingSimInfo = false
        this.updateSimInfoItemId = null
      }
    },

    // Get SIM status color
    getSimStatusColor(status) {
      const statusLower = status?.toLowerCase()
      switch (statusLower) {
        case 'active':
          return 'success'
        case 'inactive':
        case 'suspended':
          return 'warning'
        case 'terminated':
        case 'deleted':
          return 'grey'
        default:
          return 'info'
      }
    },

    goBackToSims() {
      this.$router.push({ name: 'sims' })
    },

    closeSimPanel() {
      this.simPanelVisible = false
    },

    // Show all services for the current SIM (all statuses, ordered by creation date)
    showAllSimServices() {
      if (!this.filterSimId) return

      // Select all statuses
      this.selectAllStatuses = true
      this.selectAllExceptTerminated = false
      this.selectedStatuses = this.statuses.map(s => s.id)
      this.filterActiveOnlyForSim = false

      // Sort by created_at descending (most recent first)
      this.sortBy = [{key: 'created_at', order: 'desc'}]

      // Update URL to remove status=active
      this.$router.replace({ query: { sim_id: this.filterSimId } })

      // Refresh the list
      this.search()
    },

    // Show only active services for the current SIM
    showActiveSimServicesOnly() {
      if (!this.filterSimId) return

      // Find active status ID
      const activeStatus = this.statuses.find(s => s.name === 'active')
      if (activeStatus) {
        this.selectedStatuses = [activeStatus.id]
      }
      this.selectAllStatuses = false
      this.selectAllExceptTerminated = false
      this.filterActiveOnlyForSim = true

      // Sort by expiration_date ascending
      this.sortBy = [{key: 'expiration_date', order: 'asc'}]

      // Update URL
      this.$router.replace({ query: { sim_id: this.filterSimId, status: 'active' } })

      // Refresh the list
      this.search()
    },

    // Load SIM by ID for header display when navigating with sim_id parameter
    async loadSimById(simId) {
      log("Loading SIM by ID: " + simId)
      const result = await getSimById(this.token, simId)
      if (!result.error && result.found) {
        this.simData = result.imsi
        this.simServices = result.client_services || []
        this.simAvailableServices = result.available_services || []
        log("Loaded SIM: serial=" + this.simData.serial + ", imsi=" + this.simData.imsi)
        log("Available services for SIM: " + this.simAvailableServices.length)
      } else {
        log("SIM not found or error: " + (result.message || 'unknown'))
      }
    }
  },
  async mounted() {
    const {getLoggedIn} = useGetLoggedIn()
    await getLoggedIn()
    this.token = store.getters.getToken
    log("ClientServicesList mounted, token: " + this.token)

    // Check user permissions
    const user = store.getters.getUser
    const userRoles = user?.roles || []
    this.canEditClientServices = user?.admin || userRoles.includes('edit_client_services')
    this.canRefundServices = user?.admin || userRoles.includes('refund_client_services') || userRoles.includes('financial_admin')
    log("canEditClientServices:", this.canEditClientServices)
    log("canRefundServices:", this.canRefundServices)

    // Check for cs_id query parameter to search for a specific ClientService
    const csId = this.$route.query.cs_id
    if (csId) {
      this.searchTerm = `CS:${csId}`
      // Reset all filters to ensure we find the CS regardless of its status or type
      this.selectAllStatuses = true
      this.selectAllExceptTerminated = false
      this.selectedStatuses = []
      this.serviceTypeFilter = 'all'
      // Store cs_id to expand after fetch
      this.autoExpandCsId = parseInt(csId, 10)
    }

    // Check for msisdn_id query parameter (from MsisdnsList "ShowAvailableServices")
    const msisdnIdParam = this.$route.query.msisdn_id
    if (msisdnIdParam && !csId) {
      this.searchTerm = msisdnIdParam
      this.serviceTypeFilter = 'all'
      this.includeSubclientsToggle = true
      this.selectAllStatuses = false
      this.selectAllExceptTerminated = false
      // Will set active+executed after statuses load
      this.filterStatusNames = ['active', 'executed']
    }

    // Check for search query parameter (for IMSI, MSISDN searches)
    const searchParam = this.$route.query.search
    if (searchParam && !csId && !msisdnIdParam) {
      this.searchTerm = searchParam
      // Select all statuses to ensure we find results regardless of status
      this.selectAllStatuses = true
      this.selectedStatuses = this.statuses.map(s => s.id)
    }

    // Check for sim_id query parameter (from SimsList navigation)
    const simIdParam = this.$route.query.sim_id
    if (simIdParam) {
      this.filterSimId = parseInt(simIdParam, 10)
      // Check if status=active is specified (default for sim services)
      const statusParam = this.$route.query.status
      if (statusParam === 'active') {
        // Filter by active status only - will be applied after statuses are loaded
        this.filterActiveOnlyForSim = true
        this.selectAllStatuses = false
        this.selectAllExceptTerminated = false
        this.selectedStatuses = [] // Will be set to active only after fetching
      } else {
        // Show all statuses
        this.filterActiveOnlyForSim = false
        this.selectAllStatuses = true
        this.selectAllExceptTerminated = false
        this.selectedStatuses = [] // Will be populated after fetching
      }
      // Load SIM details to show in header
      await this.loadSimById(this.filterSimId)
    }

    // clients_hierarchy will be loaded from the API response in fetchClientServices
    // Load services list for the dropdown filter
    this.loadServices()

    // Ensure Vue has processed all reactive updates before searching
    await this.$nextTick()
    log("ClientServicesList: About to search with searchTerm=" + this.searchTerm)
    await this.search()
  }
}
</script>

<style scoped>
.v-checkbox {
  margin-right: 8px;
}

.clickable-date {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
}

.clickable-date:hover {
  text-decoration-style: solid;
}

.clickable-link {
  color: #1976D2;
  cursor: pointer;
  text-decoration: underline;
}

.clickable-link:hover {
  color: #1565C0;
  text-decoration: underline;
}

.clickable-chip {
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.1s;
}

.clickable-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.sim-panel {
  border: 2px solid #1976D2;
}

.sim-service-item {
  border: 1px solid #e0e0e0;
  transition: all 0.2s;
}

.sim-service-item:hover {
  border-color: #1976D2;
}
</style>
